import { Form, Button, InputGroup } from 'react-bootstrap'

import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import StandardModal from '../../general/defaultModals/StandardModal'

export default function UserAdd({
  getUsers = null,
  triggerMode = 'button',
  showModalOverride = null,
  setShowModalOverride = null,
  navigationParent = null,
}) {

  const url = process.env.REACT_APP_URL
  const { user: authUser } = useSelector(x => x.auth)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [saveLoading, setSaveLoading] = useState(false)
  const [existingUserInfo, setExistingUserInfo] = useState(null)

  const [showModalInternal, setShowModalInternal] = useState(false)
  const isExternallyControlled = typeof setShowModalOverride === 'function'
  const showModal = showModalOverride ?? showModalInternal
  const handleShowModal = () => {
    if (isExternallyControlled) {
      setShowModalOverride(true)
      return
    }
    setShowModalInternal(true)
  }
  const handleCloseModal = () => {
    setExistingUserInfo(null)
    if (isExternallyControlled) {
      setShowModalOverride(false)
      return
    }
    setShowModalInternal(false)
  }

  const handleSubmit = async (e, confirmExistingUser = false) => {
    e.preventDefault()
    setSaveLoading(true)
    try {
      const res = await fetch(`${url}/users`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authUser.token
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          acl: 'Guest',
          confirmExistingUser
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        if (res.status === 409 && ['EXISTING_USER_CONFIRMATION_REQUIRED', 'USER_ALREADY_ASSIGNED'].includes(data?.code)) {
          setExistingUserInfo(data)
          return
        }
        const message = data?.Error || data?.message || `(${res.status}) Could not save user`
        throw new Error(message)
      }

      const displayName = `${data?.firstName || firstName} ${data?.lastName || lastName}`.trim()
      const instanceType = String(existingUserInfo?.currentInstance?.type || 'instance')
      toast.success(
        data?.addedToInstance
          ? `User ${displayName} has been added to this ${instanceType}`
          : `User ${displayName} has been created`,
        { position: 'bottom-left' }
      )
      if (getUsers) getUsers()
      handleCloseModal()
    } catch (err) {
      toast.error(String(err?.message || err), { position: 'bottom-left' })
    } finally {
      setSaveLoading(false)
    }
  }

  const instanceTypeLabel = String(existingUserInfo?.currentInstance?.type || 'instance')
    .replace(/_/g, ' ')
    .replace(/^./, (value) => value.toUpperCase())

  const formatRole = (value) => String(value || 'guest')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())


  return (
    <>
      {triggerMode === 'button' && (
        <Button variant={'success'} className='float-center' onClick={handleShowModal}>+ Add User</Button>
      )}
      {triggerMode === 'menu-item' && (
        <button className="right-menu-item right-menu-item--add" onClick={handleShowModal}>
          <i className="bi bi-person-plus"></i>
          <span>Add User</span>
        </button>
      )}
      <StandardModal
        navigationParent={navigationParent}
        show={showModal}
        handleClose={handleCloseModal}
        title={existingUserInfo ? "Existing User Found" : "Add New User"}
        style={{ maxWidth: '560px', marginTop: '8vh' }}
        ModalBodyComponent={existingUserInfo ? (
          <div>
            <p className="mb-2"><strong>User:</strong> {existingUserInfo.existingUser?.firstName} {existingUserInfo.existingUser?.lastName}</p>
            <p className="mb-3"><strong>Email:</strong> {existingUserInfo.existingUser?.email}</p>

            <p className="mb-2"><strong>Assignment status:</strong></p>
            {existingUserInfo.assignments?.length > 0 ? (
              <ul className="mb-3">
                {existingUserInfo.assignments.map((assignment) => (
                  <li key={assignment.instanceId}>
                    {assignment.instanceName} ({String(assignment.instanceType || 'instance')}) - {formatRole(assignment.role)}
                    {assignment.clubRoles?.length > 0 ? `: ${assignment.clubRoles.join(', ')}` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-3">No current instance assignments.</p>
            )}

            {existingUserInfo.currentAssignment ? (
              <p className="mb-0">
                This user is already assigned to {existingUserInfo.currentInstance?.name || `this ${instanceTypeLabel}`}.
              </p>
            ) : (
              <p className="mb-0">Would you like to add this user to this {instanceTypeLabel}?</p>
            )}
          </div>
        ) : (
          <Form>
            <InputGroup className='mb-3'>
              <Form.Control
                className='formInput'
                placeholder='First Name'
                aria-label='First Name'
                name='firstName'
                onChange={e => {
                  setFirstName(e.target.value)
                  setExistingUserInfo(null)
                }}
              />
            </InputGroup>
            <InputGroup className='mb-3'>
              <Form.Control
                className='formInput'
                placeholder='Last Name'
                aria-label='Last Name'
                name='lastName'
                onChange={e => {
                  setLastName(e.target.value)
                  setExistingUserInfo(null)
                }}
              />
            </InputGroup>
            <InputGroup className='mb-3'>
              <Form.Control
                className='formInput'
                placeholder='Email'
                aria-label='Email'
                name='email'
                onChange={e => {
                  setEmail(e.target.value)
                  setExistingUserInfo(null)
                }}
              />
            </InputGroup>
            <p>By adding the user's name and email, the user confirms that he acknowledges and agrees with our <strong>terms and conditions</strong> available on the link on the bottom right of the website.</p>
          </Form>
        )}
        ModalFooterComponent={saveLoading
          ? 'Adding user ... '
          : existingUserInfo
            ? (
              <>
                {!existingUserInfo.currentAssignment && (
                  <Button variant="success" type="button" onClick={(event) => handleSubmit(event, true)}>
                    Add to {instanceTypeLabel}
                  </Button>
                )}
                <Button variant="danger" onClick={handleCloseModal}>
                  {existingUserInfo.currentAssignment ? 'Close' : 'Cancel'}
                </Button>
              </>
            )
          : (
            <>
              <Button variant="success" type='submit' onClick={handleSubmit}>
                Add User
              </Button>
              <Button variant="danger" onClick={handleCloseModal}>
                Cancel
              </Button>
            </>
          )
        }
      />
    </>
  )
}